import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import api from "../../services/api";
import { useTranslation } from "react-i18next";
import { libelleLangue, libelleScolarite, libelleJour } from "utils/valeurs";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ["places"];

const LANGUES = ["Français", "Arabe", "Anglais", "Turc", "Berbère", "Autre"];
const DISPONIBILITES = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const NIVEAUX_SCOLAIRES = [
  { group: "Collège", options: ["6ème", "5ème", "4ème", "3ème"] },
  { group: "Lycée", options: ["2nde", "1ère", "Terminale"] },
  { group: "Supérieur", options: ["Étudiant(e)", "Diplômé(e)"] },
];

const fieldStyle = {
  width: "100%", height: "44px", padding: "0 14px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box",
  transition: "border-color 0.2s", appearance: "auto",
  fontFamily: "inherit",
};

function Section({ title, icon, children }) {
  return (
    <MDBox mb={4}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 32, height: 32, borderRadius: "8px",
          background: "linear-gradient(135deg, #1565c0, #1976d2)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 16 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 3 }} />
      {children}
    </MDBox>
  );
}

function FieldLabel({ label, required, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" fontWeight="bold" color="text" sx={{
        mb: 0.5, display: "block", textTransform: "uppercase",
        fontSize: 11, letterSpacing: "0.05em",
      }}>
        {label}{required && " *"}
      </MDTypography>
      {children}
    </MDBox>
  );
}

function InputField({ label, name, value, onChange, type = "text", placeholder, multiline, rows }) {
  if (multiline) return (
    <FieldLabel label={label}>
      <textarea name={name} value={value || ""} onChange={onChange}
        placeholder={placeholder} rows={rows || 4} autoComplete="off"
        style={{ ...fieldStyle, height: "auto", padding: "10px 14px", resize: "vertical" }}
        onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      />
    </FieldLabel>
  );
  return (
    <FieldLabel label={label}>
      <input name={name} type={type} value={value || ""} onChange={onChange}
        placeholder={placeholder} autoComplete="off" style={fieldStyle}
        onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      />
    </FieldLabel>
  );
}

function SelectField({ label, name, value, onChange, options }) {
  const { t } = useTranslation();
  return (
    <FieldLabel label={label}>
      <select name={name} value={value || ""} onChange={onChange}
        style={{ ...fieldStyle, color: value ? "#344767" : "#9ca3af" }}
        onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      >
        <option value="">{t("commun.choisir")}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FieldLabel>
  );
}

function SelectGroupField({ label, name, value, onChange, groups }) {
  const { t } = useTranslation();
  return (
    <FieldLabel label={label}>
      <select name={name} value={value || ""} onChange={onChange}
        style={{ ...fieldStyle, color: value ? "#344767" : "#9ca3af" }}
        onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      >
        <option value="">{t("commun.choisir")}</option>
        {groups.map((g) => (
          <optgroup key={g.group} label={libelleScolarite(g.group)}>
            {g.options.map((o) => <option key={o} value={o}>{libelleScolarite(o)}</option>)}
          </optgroup>
        ))}
      </select>
    </FieldLabel>
  );
}

function ChipsField({ label, values, options, onToggle, color = "#1565c0", format = (v) => v }) {
  const { t } = useTranslation();
  return (
    <FieldLabel label={label}>
      <MDBox display="flex" flexWrap="wrap" gap={1} mt={0.5}>
        {options.map((o) => {
          const selected = (values || []).includes(o);
          return (
            <MDBox key={o} onClick={() => onToggle(o)} sx={{
              padding: "6px 14px", borderRadius: "20px", border: "1px solid",
              borderColor: selected ? color : "#d2d6da",
              background: selected ? color : "#fff",
              color: selected ? "#fff" : "#344767",
              cursor: "pointer", fontSize: "13px", fontWeight: 500,
              userSelect: "none", transition: "all 0.2s",
            }}>
              {format(o)}
            </MDBox>
          );
        })}
      </MDBox>
      {(values || []).length > 0 && (
        <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
          {t("eleveForm.selectionnes", { liste: values.map(format).join(", ") })}
        </MDTypography>
      )}
    </FieldLabel>
  );
}

function ToggleField({ label, name, value, onChange }) {
  const { t } = useTranslation();
  return (
    <FieldLabel label={label}>
      <MDBox display="flex" alignItems="center" gap={1} sx={{
        height: "44px", background: "#f8faff",
        borderRadius: "8px", border: "1px solid #d2d6da", padding: "0 14px",
      }}>
        {[{ label: t("commun.oui"), val: true }, { label: t("commun.non"), val: false }].map((opt) => (
          <MDBox key={opt.label}
            onClick={() => onChange({ target: { name, value: opt.val } })}
            sx={{
              padding: "4px 18px", borderRadius: "20px", border: "1px solid",
              borderColor: value === opt.val ? "#1565c0" : "#d2d6da",
              background: value === opt.val ? "#1565c0" : "#fff",
              color: value === opt.val ? "#fff" : "#344767",
              cursor: "pointer", fontSize: "13px", fontWeight: 500,
              transition: "all 0.2s",
            }}>
            {opt.label}
          </MDBox>
        ))}
      </MDBox>
    </FieldLabel>
  );
}

function EleveForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = Boolean(id && id !== "nouveau");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [stream, setStream] = useState(null);

  const autocompleteRef = useRef(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [form, setForm] = useState({
    nom: "", prenom: "", date_naissance: "", sexe: "",
    situation_familiale: "", adresse: "", ville: "", code_postal: "",
    telephone: "", email: "", langue_parlee: [], profession: "",
    niveau_etudes: "", classe: "", precision_etudes: "",
    niveau_quoran: "", niveau_arabe: "", sait_lire_arabe: false,
    disponibilites: [], mode_cours: "", amene_par: "", commentaires: "",
    latitude: "", longitude: "", autre_langue: "", est_ancien: false, is_habachi: false, niveau_engagement: "",
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/eleves/${id}`).then(({ data }) => {
        const langues = data.langue_parlee || [];
        const autreLangue = langues.find((l) => l.startsWith("Autre - "));
        const languesClean = autreLangue
          ? [...langues.filter((l) => !l.startsWith("Autre - ")), "Autre"]
          : langues;
        setForm({
          ...data,
          langue_parlee: languesClean,
          disponibilites: data.disponibilites || [],
          autre_langue: autreLangue ? autreLangue.replace("Autre - ", "") : "",
        });
        if (data.photo) setPhotoPreview(`${data.photo}`);
      });
    }
  }, [id, isEdit]);

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      const components = place.address_components || [];
      const get = (type) => components.find((c) => c.types.includes(type))?.long_name || "";
      const adresse = [get("street_number"), get("route")].filter(Boolean).join(" ");
      const ville = get("locality") || get("administrative_area_level_2");
      const code_postal = get("postal_code");
      const lat = place.geometry?.location?.lat() || "";
      const lng = place.geometry?.location?.lng() || "";
      setForm((prev) => ({ ...prev, adresse, ville, code_postal, latitude: lat, longitude: lng }));
    }
  };

  const startWebcam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(s);
      setShowWebcam(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 100);
    } catch { alert(t("commun.photo.erreurWebcam")); }
  };

  const stopWebcam = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setShowWebcam(false);
    setStream(null);
  };

  const takePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
      setPhotoFile(file);
      setPhotoPreview(canvas.toDataURL("image/jpeg"));
    }, "image/jpeg");
    stopWebcam();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleChip = (field, value) => {
    const current = form[field] || [];
    const updated = current.includes(value)
      ? current.filter((x) => x !== value)
      : [...current, value];
    setForm((prev) => ({ ...prev, [field]: updated }));
  };

  const showPrecisionEtudes = ["Étudiant(e)", "Diplômé(e)"].includes(form.niveau_etudes);
  const precisionLabel = form.niveau_etudes === "Étudiant(e)" ? t("eleveForm.filiere") : t("eleveForm.diplome");
  const precisionPlaceholder = form.niveau_etudes === "Étudiant(e)"
    ? t("eleveForm.filierePlaceholder")
    : t("eleveForm.diplomePlaceholder");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim()) {
      setError(t("eleveForm.erreurPrenom"));
      return;
    }
    setError("");
    setSaving(true);

    const cleanForm = Object.fromEntries(
      Object.entries(form).map(([key, val]) => {
        if (val === "" || val === undefined) return [key, null];
        return [key, val];
      })
    );

    // Remplacer "Autre" par "Autre - valeur" dans langue_parlee
    const languesFinal = (form.langue_parlee || []).map((l) => {
      if (l === "Autre" && form.autre_langue?.trim()) return `${form.autre_langue.trim()}`;
      return l;
    });
    cleanForm.langue_parlee = languesFinal;
    cleanForm.disponibilites = form.disponibilites || [];
    delete cleanForm.autre_langue;

    try {
      let savedId = id;
      if (isEdit) {
        await api.put(`/eleves/${id}`, cleanForm);
      } else {
        const { data } = await api.post("/eleves", cleanForm);
        savedId = data.id;
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append("photo", photoFile);
        await api.post(`/eleves/${savedId}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      navigate("/eleves");
    } catch (err) {
      setError(err.response?.data?.message || t("commun.erreurEnregistrement"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={10}>

            <MDBox mb={3} display="flex" alignItems="center" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/eleves")}
                sx={{ borderRadius: "10px", px: 2, whiteSpace: "nowrap" }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> {t("commun.retourListe")}
              </MDButton>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#344767" }}>
                {isEdit ? t("eleveForm.titreModifier") : t("eleves.ajouter")}
              </MDTypography>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDBox mx={2} mt={-3} py={2} px={3} sx={{
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
              }}>
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  {t("eleveForm.bandeau")}
                </MDTypography>
              </MDBox>

              <MDBox pt={4} pb={4} px={4}>
                {error && (
                  <MDBox mb={3} p={2}
                    sx={{ background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2" }}>
                    <MDTypography variant="caption" color="error" fontWeight="medium">
                      ⚠️ {error}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox component="form" onSubmit={handleSubmit} autoComplete="off">

                  {/* ── SECTION 1 : PHOTO ── */}
                  <Section title={t("commun.photo.titre")} icon="camera_alt">
                    <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
                      <MDBox sx={{
                        width: 110, height: 110, borderRadius: "50%",
                        border: "3px solid #e3f2fd", overflow: "hidden",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "#f5f7ff", flexShrink: 0,
                      }}>
                        {photoPreview ? (
                          <img src={photoPreview} alt={t("commun.photo.apercu")}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <Icon style={{ fontSize: 50, color: "#bbdefb" }}>person</Icon>
                        )}
                      </MDBox>
                      <MDBox display="flex" flexDirection="column" gap={1}>
                        <MDButton variant="outlined" color="info" size="small" onClick={startWebcam}>
                          <Icon>camera_alt</Icon>&nbsp; {t("commun.photo.webcam")}
                        </MDButton>
                        <MDButton variant="outlined" color="secondary" size="small" component="label">
                          <Icon>upload</Icon>&nbsp; {t("commun.photo.uploader")}
                          <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                        </MDButton>
                        {photoPreview && (
                          <MDButton variant="outlined" color="error" size="small"
                            onClick={() => { setPhotoPreview(null); setPhotoFile(null); }}>
                            <Icon>cancel</Icon>&nbsp; {t("commun.photo.supprimer")}
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>
                    {showWebcam && (
                      <MDBox mt={2}>
                        <video ref={videoRef} autoPlay
                          style={{ width: "100%", maxWidth: 400, borderRadius: 10 }} />
                        <canvas ref={canvasRef} style={{ display: "none" }} />
                        <MDBox display="flex" gap={2} mt={1}>
                          <MDButton variant="gradient" color="success" size="small" onClick={takePhoto}>
                            <Icon>camera</Icon>&nbsp; {t("commun.photo.prendre")}
                          </MDButton>
                          <MDButton variant="outlined" color="error" size="small" onClick={stopWebcam}>
                            {t("commun.annuler")}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    )}
                  </Section>

                  {/* ── SECTION 2 : IDENTITÉ ── */}
                  <Section title={t("eleves.sections.identite")} icon="person">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label={`${t("champs.prenom")} *`}>
                          <input name="prenom" value={form.prenom} onChange={handleChange}
                            placeholder={t("eleveForm.prenomPlaceholder")} autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                        </FieldLabel>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InputField label={t("champs.nom")} name="nom" value={form.nom}
                          onChange={handleChange} placeholder={t("eleveForm.nomPlaceholder")} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FieldLabel label={t("champs.dateNaissance")}>
                          <input name="date_naissance" type="date"
                            value={form.date_naissance || ""} onChange={handleChange}
                            autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                        </FieldLabel>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SelectField label={t("champs.sexe")} name="sexe" value={form.sexe}
                          onChange={handleChange}
                          options={[
                            { value: "homme", label: t("valeurs.sexe.homme") },
                            { value: "femme", label: t("valeurs.sexe.femme") },
                          ]} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SelectField label={t("champs.situationFamiliale")} name="situation_familiale"
                          value={form.situation_familiale} onChange={handleChange}
                          options={[
                            { value: "celibataire", label: t("valeurs.situation.celibataire") },
                            { value: "marie", label: t("valeurs.situation.marie") },
                            { value: "divorce", label: t("valeurs.situation.divorce") },
                          ]} />
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── SECTION 3 : COORDONNÉES ── */}
                  <Section title={t("eleves.sections.coordonnees")} icon="contact_phone">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FieldLabel label={t("champs.adressePostale")}>
                          {isLoaded ? (
                            <Autocomplete
                              onLoad={(ref) => (autocompleteRef.current = ref)}
                              onPlaceChanged={onPlaceChanged}
                              options={{ componentRestrictions: { country: "fr" }, types: ["address"] }}
                            >
                              <input name="adresse" value={form.adresse || ""}
                                onChange={handleChange}
                                placeholder={t("commun.adresse.placeholder")}
                                autoComplete="off" style={fieldStyle}
                                onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
                                onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                            </Autocomplete>
                          ) : (
                            <input name="adresse" value={form.adresse || ""}
                              onChange={handleChange} placeholder={t("champs.adressePostale")}
                              autoComplete="off" style={fieldStyle} />
                          )}
                        </FieldLabel>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <InputField label={t("champs.ville")} name="ville" value={form.ville} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <InputField label={t("champs.codePostal")} name="code_postal"
                          value={form.code_postal} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InputField label={t("champs.telephone")} name="telephone"
                          value={form.telephone} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label={t("champs.email")}>
                          <input name="email" type="text" value={form.email || ""}
                            onChange={handleChange} placeholder="exemple@email.com"
                            autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                        </FieldLabel>
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── SECTION 4 : LANGUES ── */}
                  <Section title={t("champs.languesParlees")} icon="language">
                    <ChipsField
                      label={t("eleveForm.selectLangues")}
                      values={form.langue_parlee}
                      options={LANGUES}
                      format={libelleLangue}
                      onToggle={(l) => toggleChip("langue_parlee", l)}
                    />
                    {(form.langue_parlee || []).includes("Autre") && (
                      <MDBox mt={1} sx={{
                        background: "#f0f7ff", borderRadius: "10px",
                        padding: "16px", border: "1px solid #bbdefb",
                      }}>
                        <FieldLabel label={t("eleveForm.precisezLangue")}>
                          <input
                            type="text"
                            placeholder={t("eleveForm.autreLanguePlaceholder")}
                            autoComplete="off"
                            style={fieldStyle}
                            value={form.autre_langue || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setForm((prev) => ({
                                ...prev,
                                autre_langue: val,
                              }));
                            }}
                            onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                        {form.autre_langue && (
                          <MDTypography variant="caption" sx={{ color: "#1565c0", fontWeight: 600 }}>
                            {t("eleveForm.langueAjoutee", { langue: form.autre_langue })}
                          </MDTypography>
                        )}
                      </MDBox>
                    )}
                  </Section>

                  {/* ── SECTION 5 : SCOLARITÉ & PROFESSION ── */}
                  <Section title={t("eleves.sections.scolarite")} icon="work">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <SelectGroupField label={t("champs.niveauEtudes")} name="niveau_etudes"
                          value={form.niveau_etudes} onChange={handleChange}
                          groups={NIVEAUX_SCOLAIRES} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <InputField label={t("eleveForm.classePrecise")} name="classe"
                          value={form.classe} onChange={handleChange}
                          placeholder={t("eleveForm.classePlaceholder")} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <InputField label={t("eleveForm.professionMetier")} name="profession"
                          value={form.profession} onChange={handleChange}
                          placeholder={t("eleveForm.professionPlaceholder")} />
                      </Grid>
                      {showPrecisionEtudes && (
                        <Grid item xs={12}>
                          <MDBox sx={{
                            background: "#f0f7ff", borderRadius: "10px",
                            padding: "16px", border: "1px solid #bbdefb",
                          }}>
                            <InputField label={precisionLabel} name="precision_etudes"
                              value={form.precision_etudes} onChange={handleChange}
                              placeholder={precisionPlaceholder} />
                          </MDBox>
                        </Grid>
                      )}
                    </Grid>
                  </Section>

                  {/* ── SECTION 6 : PARCOURS RELIGIEUX ── */}
                  <Section title={t("eleves.sections.religieux")} icon="menu_book">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <SelectField label={t("champs.niveauQuoran")} name="niveau_quoran"
                          value={form.niveau_quoran} onChange={handleChange}
                          options={[
                            { value: "debutant", label: t("valeurs.niveau.debutant") },
                            { value: "intermediaire", label: t("valeurs.niveau.intermediaire") },
                            { value: "avance", label: t("valeurs.niveau.avance") },
                          ]} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SelectField label={t("champs.niveauArabe")} name="niveau_arabe"
                          value={form.niveau_arabe} onChange={handleChange}
                          options={[
                            { value: "debutant", label: t("valeurs.niveau.debutant") },
                            { value: "intermediaire", label: t("valeurs.niveau.intermediaire") },
                            { value: "avance", label: t("valeurs.niveau.avance") },
                          ]} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ToggleField label={t("eleveForm.saitLire")} name="sait_lire_arabe"
                          value={form.sait_lire_arabe} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ToggleField label={t("eleveForm.ancien")} name="est_ancien"
                          value={form.est_ancien} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ToggleField label={t("champs.habachi")} name="is_habachi"
                          value={form.is_habachi} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SelectField label={t("champs.niveauEngagement")} name="niveau_engagement"
                          value={form.niveau_engagement || ""} onChange={handleChange}
                          options={[
                            { value: "1", label: t("valeurs.engagement.1") },
                            { value: "2", label: t("valeurs.engagement.2") },
                            { value: "3", label: t("valeurs.engagement.3") },
                            { value: "4", label: t("valeurs.engagement.4") },
                            { value: "5", label: t("valeurs.engagement.5") },
                          ]} />
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── SECTION 7 : PRATIQUE ── */}
                  <Section title={t("eleves.sections.pratique")} icon="schedule">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <ChipsField label={t("champs.disponibilites")} values={form.disponibilites}
                          options={DISPONIBILITES}
                          format={libelleJour}
                          onToggle={(d) => toggleChip("disponibilites", d)}
                          color="#2e7d32" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SelectField label={t("eleveForm.modePrefere")} name="mode_cours"
                          value={form.mode_cours} onChange={handleChange}
                          options={[
                            { value: "presentiel", label: t("valeurs.mode.presentiel") },
                            { value: "enligne", label: t("valeurs.mode.enligne") },
                            { value: "les_deux", label: t("valeurs.mode.les_deux") },
                          ]} />
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── SECTION 8 : INFORMATIONS COMPLÉMENTAIRES ── */}
                  <Section title={t("eleves.sections.complementaires")} icon="info">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <InputField label={t("champs.amenePar")} name="amene_par"
                          value={form.amene_par} onChange={handleChange}
                          placeholder={t("eleveForm.amenePlaceholder")} />
                      </Grid>
                      <Grid item xs={12}>
                        <InputField label={t("eleveForm.commentairesNotes")} name="commentaires"
                          value={form.commentaires} onChange={handleChange}
                          multiline rows={4} placeholder={t("eleveForm.commentairesPlaceholder")} />
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── BOUTONS ── */}
                  <MDBox display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <MDButton variant="outlined" color="secondary"
                      onClick={() => navigate("/eleves")}>
                      {t("commun.annuler")}
                    </MDButton>
                    <MDButton variant="gradient" color="info" type="submit"
                      disabled={saving} sx={{ px: 4 }}>
                      {saving ? t("commun.enregistrement") : isEdit ? t("commun.mettreAJour") : t("eleveForm.enregistrer")}
                    </MDButton>
                  </MDBox>

                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EleveForm;